// A Shima Seiki .DAT, as LOOP writes it: a 0x600-byte header, holding the picture's corners and its
// colour table (red, green and blue at 0x200, 0x300 and 0x400, each pair of bytes swapped), then the
// picture as run-length pairs of (colour number, count), bottom row first. Each row is one pass of
// the carriage; the needles are the middle columns, with the option lines down either side.

const HEADER = 0x600;
const MAGIC = 1000;

export type DatPicture = {
  readonly width: number;
  readonly height: number;
  // 256 colours, three bytes each
  readonly palette: Uint8Array;
  // the colour number of every cell, row by row from the bottom: row 0 is the first the machine knits
  readonly codes: Uint8Array;
};

// scripts/knit-along/dat.py's labels for one program: where its needles and option lines are, what
// each option line sets, and what each colour in the needles means.
export type DatLabels = {
  readonly needleColumns: readonly [number, number];
  readonly needleZero: number;
  readonly directionColumn: number;
  readonly directions: Readonly<Record<string, string>>;
  readonly optionLines: readonly {
    readonly name: string;
    readonly label: string | null;
    readonly labelColumn: number;
    readonly valueColumn: number;
    readonly values: Readonly<Record<string, string>>;
  }[];
  readonly codes: Readonly<Record<string, { readonly label: string; readonly count: number }>>;
};

export const readDat = (buffer: ArrayBuffer): DatPicture => {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  if (bytes.length < HEADER || view.getUint16(8, true) !== MAGIC || view.getUint16(16, true) !== MAGIC) {
    throw new Error("Not a .DAT file");
  }
  const width = view.getUint16(4, true) - view.getUint16(0, true) + 1;
  const height = view.getUint16(6, true) - view.getUint16(2, true) + 1;
  const palette = new Uint8Array(256 * 3);
  for (let channel = 0; channel < 3; channel += 1) {
    for (let index = 0; index < 256; index += 1) {
      palette[index * 3 + channel] = bytes[0x200 + channel * 0x100 + (index ^ 1)];
    }
  }
  const codes = new Uint8Array(width * height);
  let cell = 0;
  for (let at = HEADER; at + 1 < bytes.length && cell < codes.length; at += 2) {
    const end = Math.min(cell + bytes[at + 1], codes.length);
    codes.fill(bytes[at], cell, end);
    cell = end;
  }
  return { width, height, palette, codes };
};

// The picture as RGBA, top row first, as a canvas draws it.
export const datPixels = ({ width, height, palette, codes }: DatPicture): Uint8ClampedArray<ArrayBuffer> => {
  const pixels = new Uint8ClampedArray(width * height * 4);
  for (let row = 0; row < height; row += 1) {
    const from = row * width;
    const to = (height - 1 - row) * width;
    for (let column = 0; column < width; column += 1) {
      const code = codes[from + column];
      const at = (to + column) * 4;
      pixels[at] = palette[code * 3];
      pixels[at + 1] = palette[code * 3 + 1];
      pixels[at + 2] = palette[code * 3 + 2];
      pixels[at + 3] = 255;
    }
  }
  return pixels;
};

export type DatCell = {
  // counted from 0 at the bottom, as the knitcode and knitout count them
  readonly row: number;
  readonly code: number;
  // what the cell says: a needle's instruction, or an option line's setting
  readonly place: string;
  readonly meaning: string;
};

// What one cell of the picture tells the machine; null in the margins.
export const datCell = (picture: DatPicture, labels: DatLabels, column: number, row: number): DatCell | null => {
  if (column < 0 || column >= picture.width || row < 0 || row >= picture.height) return null;
  const code = picture.codes[row * picture.width + column];
  const cell = { row, code };
  const [first, last] = labels.needleColumns;
  if (column >= first && column <= last) {
    const needle = column - labels.needleZero;
    return {
      ...cell,
      place: needle >= 0 ? `Needle ${needle + 1}` : "Before the needles",
      meaning: labels.codes[code]?.label ?? `Colour ${code}`,
    };
  }
  if (column === labels.directionColumn) {
    return { ...cell, place: "Carriage direction", meaning: labels.directions[code] ?? `Colour ${code}` };
  }
  const line = labels.optionLines.find((entry) => entry.valueColumn === column || entry.labelColumn === column);
  if (!line) return null;
  if (column === line.labelColumn) {
    return { ...cell, place: `Option line ${line.name}`, meaning: "Its number, marking the column" };
  }
  return {
    ...cell,
    place: `Option line ${line.name}${line.label ? ` · ${line.label}` : ""}`,
    meaning: code === 0 ? "Not set" : (line.values[code] ?? `Set to ${code}`),
  };
};
